import { useEffect, useRef, useState } from 'react';
import { SsoLoginMode, useCheckSsoLazyQuery } from '../../generated/hooks';

const DEBOUNCE_MS = 300;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+/;

interface IPendingValue {
  email: string;
  promise: Promise<SsoLoginMode>;
  resolve: (value: SsoLoginMode) => void;
}

export interface SsoLoginModeResult {
  /** null while the check for the current email is still in flight */
  value: SsoLoginMode | null;
  valuePromise: Promise<SsoLoginMode> | null;
}

export function normalizeSsoEmail(email?: string | null): string {
  return email?.trim().toLowerCase() || '';
}

function isValidEmailFormat(email: string): boolean {
  return EMAIL_REGEX.test(email);
}

export async function resolveSsoLoginMode({ value, valuePromise }: SsoLoginModeResult): Promise<SsoLoginMode> {
  if (value !== null) {
    return value;
  }
  return valuePromise ? valuePromise : SsoLoginMode.None;
}

export function useSsoLoginMode(email?: string | null): SsoLoginModeResult {
  const normalizedEmail = normalizeSsoEmail(email);
  const initialEmailRef = useRef(normalizedEmail);
  const latestEmailRef = useRef(normalizedEmail);
  const pendingValueRef = useRef<IPendingValue | null>(null);
  const debounceTimeoutRef = useRef<number | null>(null);
  const cacheRef = useRef<Map<string, SsoLoginMode>>(new Map());
  const [value, setValue] = useState<SsoLoginMode | null>(SsoLoginMode.None);
  const [checkSsoQuery] = useCheckSsoLazyQuery();

  useEffect(() => {
    latestEmailRef.current = normalizedEmail;
  }, [normalizedEmail]);

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current !== null) {
        window.clearTimeout(debounceTimeoutRef.current);
      }
      pendingValueRef.current?.resolve(SsoLoginMode.None);
      pendingValueRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (debounceTimeoutRef.current !== null) {
      window.clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }

    if (pendingValueRef.current && pendingValueRef.current.email !== normalizedEmail) {
      pendingValueRef.current.resolve(SsoLoginMode.None);
      pendingValueRef.current = null;
    }

    if (!normalizedEmail || !isValidEmailFormat(normalizedEmail)) {
      setValue(SsoLoginMode.None);
      return;
    }

    const cachedValue = cacheRef.current.get(normalizedEmail);
    if (cachedValue !== undefined) {
      setValue(cachedValue);
      return;
    }

    let resolvePendingValue!: (value: SsoLoginMode) => void;
    const valuePromise = new Promise<SsoLoginMode>((resolve) => {
      resolvePendingValue = resolve;
    });

    pendingValueRef.current = {
      email: normalizedEmail,
      promise: valuePromise,
      resolve: resolvePendingValue,
    };
    setValue(null);

    const delay = normalizedEmail === initialEmailRef.current ? 0 : DEBOUNCE_MS;
    debounceTimeoutRef.current = window.setTimeout(async () => {
      let nextValue = SsoLoginMode.None;

      try {
        const result = await checkSsoQuery({ variables: { email: normalizedEmail } });
        nextValue = result.data?.checkSso ?? SsoLoginMode.None;
      } catch {
        nextValue = SsoLoginMode.None;
      }

      cacheRef.current.set(normalizedEmail, nextValue);

      if (pendingValueRef.current?.email === normalizedEmail) {
        pendingValueRef.current.resolve(nextValue);
        pendingValueRef.current = null;
      } else {
        resolvePendingValue(nextValue);
      }

      if (latestEmailRef.current === normalizedEmail) {
        setValue(nextValue);
      }

      debounceTimeoutRef.current = null;
    }, delay);
  }, [checkSsoQuery, normalizedEmail]);

  return {
    value,
    valuePromise: value === null ? pendingValueRef.current?.promise ?? null : null,
  };
}
